/**
 * Vendor Service - CRUD operations for vendors
 * Handles vendor management including approval/rejection workflow
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where,
  orderBy,
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import { 
  Vendor, 
  CreateVendorInput, 
  UpdateVendorInput, 
  VendorFilters, 
  VendorStats 
} from '../../types/vendor';

class VendorService {
  private readonly COLLECTION = 'vendors';

  /**
   * Get collection reference with tenant scoping
   */
  private getCollectionRef() {
    const companyId = getCompanyId();
    return collection(db, `companies/${companyId}/${this.COLLECTION}`);
  }

  /**
   * Get vendor by ID
   */
  async getVendor(vendorId: string): Promise<Vendor | null> {
    try {
      const companyId = getCompanyId();
      const vendorRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, vendorId);
      const vendorSnap = await getDoc(vendorRef);

      if (!vendorSnap.exists()) {
        return null;
      }

      return this.convertFirestoreDoc(vendorSnap);
    } catch (error) {
      console.error('Error getting vendor:', error);
      throw error;
    }
  }

  /**
   * Get all vendors with optional filters
   */
  async getVendors(filters?: VendorFilters): Promise<Vendor[]> {
    try {
      const collectionRef = this.getCollectionRef();
      let q = query(collectionRef, where('deleted', '==', false), orderBy('createdAt', 'desc'));

      // Apply status filter
      if (filters?.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        q = query(collectionRef, where('status', 'in', statuses), where('deleted', '==', false));
      }

      const querySnapshot = await getDocs(q);
      let vendors = querySnapshot.docs.map(doc => this.convertFirestoreDoc(doc));

      // Client-side filters
      if (filters?.services?.length) {
        vendors = vendors.filter(v => 
          v.services.some(s => filters.services!.includes(s))
        );
      }

      if (filters?.search) {
        const search = filters.search.toLowerCase();
        vendors = vendors.filter(v => 
          v.name.toLowerCase().includes(search) ||
          v.contactName.toLowerCase().includes(search) ||
          v.email.toLowerCase().includes(search) ||
          v.phone.includes(search)
        );
      }

      if (filters?.minRating) {
        vendors = vendors.filter(v => 
          v.rating && v.rating.overall >= filters.minRating!
        );
      }

      if (filters?.hasInsurance) {
        vendors = vendors.filter(v => 
          v.generalLiability && v.workersCompensation
        );
      }

      if (filters?.offers24_7Emergency !== undefined) {
        vendors = vendors.filter(v => 
          v.offers24_7Emergency === filters.offers24_7Emergency
        );
      }

      if (filters?.onboardedVia) {
        vendors = vendors.filter(v => 
          v.onboardedVia === filters.onboardedVia
        );
      }

      return vendors;
    } catch (error) {
      console.error('Error getting vendors:', error);
      throw error;
    }
  }

  /**
   * Create new vendor
   */
  async createVendor(
    input: CreateVendorInput, 
    userId: string, 
    userName: string
  ): Promise<Vendor> {
    try {
      const companyId = getCompanyId();
      const collectionRef = this.getCollectionRef();

      const vendor: Omit<Vendor, 'id'> = {
        companyId,
        name: input.name,
        dbaName: input.dbaName,
        displayName: input.dbaName || input.name,
        contactName: input.contactName,
        contactTitle: input.contactTitle,
        phone: input.phone,
        alternatePhone: input.alternatePhone,
        email: input.email,
        website: input.website,
        physicalAddress: input.physicalAddress,
        mailingAddress: input.mailingAddress,
        services: input.services,
        otherServiceDescription: input.otherServiceDescription,
        serviceAreaRadius: input.serviceAreaRadius,
        offers24_7Emergency: input.offers24_7Emergency || false,
        emergencyPhone: input.emergencyPhone,
        status: 'active', // Manual creation = auto-approved
        approvedBy: userId,
        approvedByName: userName,
        approvedAt: new Date(),
        paymentTerms: input.paymentTerms,
        preferredPaymentMethod: input.preferredPaymentMethod,
        w9Uploaded: false,
        licenses: [],
        osha30Certified: false,
        otherCertifications: [],
        references: [],
        performanceMetrics: {
          jobsCompleted: 0,
          jobsInProgress: 0,
          totalRevenue: 0,
          averageInvoiceAmount: 0,
          onTimeCompletionRate: 100,
          customerSatisfaction: 100,
        },
        onboardedVia: 'manual',
        onboardedBy: userId,
        onboardedByName: userName,
        profileCompleteness: this.calculateProfileCompleteness(input as any),
        acceptedTerms: false,
        notes: input.notes,
        tags: [],
        createdAt: new Date(),
        createdBy: userId,
        createdByName: userName,
        updatedAt: new Date(),
        deleted: false,
      };

      const docRef = await addDoc(collectionRef, this.convertToFirestore(vendor));
      
      return {
        ...vendor,
        id: docRef.id,
      };
    } catch (error) {
      console.error('Error creating vendor:', error);
      throw error;
    }
  }

  /**
   * Update vendor
   */
  async updateVendor(
    vendorId: string, 
    updates: UpdateVendorInput,
    userId: string,
    userName: string
  ): Promise<Vendor> {
    try {
      const companyId = getCompanyId();
      const vendorRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, vendorId);

      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updatedByName: userName,
      };

      // Update display name if name or dbaName changed
      if (updates.name || updates.dbaName !== undefined) {
        const vendor = await this.getVendor(vendorId);
        if (vendor) {
          updateData.displayName = updates.dbaName || updates.name || vendor.name;
        }
      }

      await updateDoc(vendorRef, this.convertToFirestore(updateData));

      const updatedVendor = await this.getVendor(vendorId);
      if (!updatedVendor) {
        throw new Error('Vendor not found after update');
      }

      return updatedVendor;
    } catch (error) {
      console.error('Error updating vendor:', error);
      throw error;
    }
  }

  /**
   * Approve pending vendor
   */
  async approveVendor(
    vendorId: string,
    userId: string,
    userName: string
  ): Promise<Vendor> {
    try {
      const companyId = getCompanyId();
      const vendorRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, vendorId);

      await updateDoc(vendorRef, {
        status: 'active',
        approvedBy: userId,
        approvedByName: userName,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updatedByName: userName,
      });

      const updatedVendor = await this.getVendor(vendorId);
      if (!updatedVendor) {
        throw new Error('Vendor not found after approval');
      }

      return updatedVendor;
    } catch (error) {
      console.error('Error approving vendor:', error);
      throw error;
    }
  }

  /**
   * Reject pending vendor
   */
  async rejectVendor(
    vendorId: string,
    userId: string,
    userName: string,
    reason?: string
  ): Promise<void> {
    try {
      const companyId = getCompanyId();
      const vendorRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, vendorId);

      await updateDoc(vendorRef, {
        status: 'rejected',
        rejectedBy: userId,
        rejectedByName: userName,
        rejectedAt: serverTimestamp(),
        rejectionReason: reason || 'No reason provided',
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updatedByName: userName,
      });
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      throw error;
    }
  }

  /**
   * Soft delete vendor
   */
  async deleteVendor(
    vendorId: string,
    userId: string
  ): Promise<void> {
    try {
      const companyId = getCompanyId();
      const vendorRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, vendorId);

      await updateDoc(vendorRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: userId,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error deleting vendor:', error);
      throw error;
    }
  }

  /**
   * Get vendor statistics
   */
  async getStats(): Promise<VendorStats> {
    try {
      const vendors = await this.getVendors();

      const stats: VendorStats = {
        total: vendors.length,
        active: vendors.filter(v => v.status === 'active').length,
        inactive: vendors.filter(v => v.status === 'inactive').length,
        pending: vendors.filter(v => v.status === 'pending_approval').length,
        suspended: vendors.filter(v => v.status === 'suspended').length,
        averageRating: 0,
        totalJobsCompleted: 0,
        totalRevenue: 0,
      };

      const ratingsSum = vendors.reduce((sum, v) => sum + (v.rating?.overall || 0), 0);
      const vendorsWithRatings = vendors.filter(v => v.rating).length;
      stats.averageRating = vendorsWithRatings > 0 ? ratingsSum / vendorsWithRatings : 0;

      stats.totalJobsCompleted = vendors.reduce((sum, v) => sum + v.performanceMetrics.jobsCompleted, 0);
      stats.totalRevenue = vendors.reduce((sum, v) => sum + v.performanceMetrics.totalRevenue, 0);

      return stats;
    } catch (error) {
      console.error('Error getting vendor stats:', error);
      throw error;
    }
  }

  /**
   * Calculate profile completeness percentage
   */
  private calculateProfileCompleteness(vendor: Partial<Vendor>): number {
    let score = 0;
    const weights: { [key: string]: number } = {
      name: 5,
      contactName: 5,
      phone: 5,
      email: 5,
      physicalAddress: 10,
      services: 10,
      paymentTerms: 5,
      preferredPaymentMethod: 5,
      generalLiability: 15,
      workersCompensation: 15,
      licenses: 10,
      references: 10,
      w9Uploaded: 5,
    };

    if (vendor.name) score += weights.name;
    if (vendor.contactName) score += weights.contactName;
    if (vendor.phone) score += weights.phone;
    if (vendor.email) score += weights.email;
    if (vendor.physicalAddress) score += weights.physicalAddress;
    if (vendor.services?.length) score += weights.services;
    if (vendor.paymentTerms) score += weights.paymentTerms;
    if (vendor.preferredPaymentMethod) score += weights.preferredPaymentMethod;
    if (vendor.generalLiability) score += weights.generalLiability;
    if (vendor.workersCompensation) score += weights.workersCompensation;
    if (vendor.licenses?.length) score += weights.licenses;
    if (vendor.references?.length) score += weights.references;
    if (vendor.w9Uploaded) score += weights.w9Uploaded;

    return Math.round(score);
  }

  /**
   * Convert Firestore doc to Vendor
   */
  private convertFirestoreDoc(doc: any): Vendor {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      approvedAt: data.approvedAt?.toDate(),
      rejectedAt: data.rejectedAt?.toDate(),
      suspendedAt: data.suspendedAt?.toDate(),
      deletedAt: data.deletedAt?.toDate(),
      acceptedTermsAt: data.acceptedTermsAt?.toDate(),
      generalLiability: data.generalLiability ? {
        ...data.generalLiability,
        expirationDate: data.generalLiability.expirationDate?.toDate(),
      } : undefined,
      workersCompensation: data.workersCompensation ? {
        ...data.workersCompensation,
        expirationDate: data.workersCompensation.expirationDate?.toDate(),
      } : undefined,
      licenses: (data.licenses || []).map((license: any) => ({
        ...license,
        expirationDate: license.expirationDate?.toDate(),
      })),
      performanceMetrics: {
        ...data.performanceMetrics,
        lastJobDate: data.performanceMetrics?.lastJobDate?.toDate(),
        firstJobDate: data.performanceMetrics?.firstJobDate?.toDate(),
      },
    } as Vendor;
  }

  /**
   * Convert Vendor to Firestore format
   */
  private convertToFirestore(data: any): any {
    const converted = { ...data };
    
    // Convert Dates to Timestamps
    if (data.createdAt instanceof Date) converted.createdAt = Timestamp.fromDate(data.createdAt);
    if (data.updatedAt instanceof Date) converted.updatedAt = Timestamp.fromDate(data.updatedAt);
    if (data.approvedAt instanceof Date) converted.approvedAt = Timestamp.fromDate(data.approvedAt);
    if (data.rejectedAt instanceof Date) converted.rejectedAt = Timestamp.fromDate(data.rejectedAt);
    if (data.suspendedAt instanceof Date) converted.suspendedAt = Timestamp.fromDate(data.suspendedAt);
    if (data.deletedAt instanceof Date) converted.deletedAt = Timestamp.fromDate(data.deletedAt);
    if (data.acceptedTermsAt instanceof Date) converted.acceptedTermsAt = Timestamp.fromDate(data.acceptedTermsAt);

    if (data.generalLiability?.expirationDate instanceof Date) {
      converted.generalLiability.expirationDate = Timestamp.fromDate(data.generalLiability.expirationDate);
    }

    if (data.workersCompensation?.expirationDate instanceof Date) {
      converted.workersCompensation.expirationDate = Timestamp.fromDate(data.workersCompensation.expirationDate);
    }

    if (data.licenses) {
      converted.licenses = data.licenses.map((license: any) => ({
        ...license,
        expirationDate: license.expirationDate instanceof Date 
          ? Timestamp.fromDate(license.expirationDate)
          : license.expirationDate,
      }));
    }

    if (data.performanceMetrics) {
      const metrics = { ...data.performanceMetrics };
      if (metrics.lastJobDate instanceof Date) {
        metrics.lastJobDate = Timestamp.fromDate(metrics.lastJobDate);
      }
      if (metrics.firstJobDate instanceof Date) {
        metrics.firstJobDate = Timestamp.fromDate(metrics.firstJobDate);
      }
      converted.performanceMetrics = metrics;
    }

    return converted;
  }
}

export const vendorService = new VendorService();
